import _ from 'lodash';
import { hideModal } from 'models/modals';
import { type ReactNode, useEffect } from 'react';
import { Modal, ModalProps } from 'react-bootstrap';
import styled, { css } from 'styled-components';

import { TExtra } from './StyledTemplates';

import ModalsService from 'services/ModalsService';

import useModal from 'hooks/useModal';

import crossBlack from 'assets/icons/cross.svg';
import crossWhite from 'assets/icons/cross_white.svg';

import { type RegisteredModals } from 'types/Modals';

/*
    TODO: Unable using ref in children components
*/

type PopUpWraperProps = Omit<ModalProps, 'size'> & {
    name: RegisteredModals;
    extra?: TExtra;
    preventClosing?: boolean;
    withCross?: boolean;
    whiteCross?: boolean;
    onClickOutside?: () => void;
    onOpen?: () => void;
    onClose?: () => void;
    children?: ReactNode | undefined;
    backdropClassName?: string;
    size?: 'sm' | 'lg' | 'xl' | 'md';
};

const PopUpWrapper = (props: PopUpWraperProps) => {
    const {
        name,
        preventClosing = false,
        withCross = true,
        whiteCross = false,
        onClickOutside = () => {},
        onOpen = () => {},
        onClose = () => {},
        dialogClassName = '',
    } = props;

    const { isOpened, shouldMount, close, suppressed } = useModal(name, { onOpen, onClose });

    useEffect(() => {
        ModalsService.setPreventClosing(name, preventClosing);
    }, [preventClosing]);

    useEffect(() => {
        return () => {
            hideModal(name);
        };
    }, []);

    if (!shouldMount) {
        return null;
    }

    return (
        <StyledModal
            show={isOpened}
            onHide={() => {
                close();
                onClickOutside();
            }}
            className={dialogClassName}
            $suppressed={suppressed}
            {..._.omit(props, [
                'whiteCross',
                'suppressed',
                'preventClosing',
                'withCross',
                'dialogClassName',
                'onClickOutside',
            ])}
        >
            {withCross && (
                <Cross
                    data-qaid="close_modal_cross"
                    onClick={() => {
                        close();
                        onClickOutside();
                    }}
                    white={whiteCross}
                />
            )}
            {props.children}
        </StyledModal>
    );
};

const StyledModal = styled(Modal)<{
    extra?: TExtra;
    $suppressed?: boolean;
}>`
    .modal-dialog {
        ${({ $suppressed: isSuppressed }) => {
            if (isSuppressed) {
                return css`
                    top: 0;
                    transform: translateY(-100%) !important;
                    position: absolute;
                    opacity: 0.5;
                `;
            }
        }}
    }

    ${({ extra }) => extra}
`;

const Cross = styled.button<{ white?: boolean }>`
    position: absolute;
    top: 25px;
    right: 25px;
    z-index: 2;
    cursor: pointer;
    outline: none;
    border: 0;
    border-radius: 4px;
    width: 24px;
    height: 24px;
    background: url('${({ white }) => (white ? crossWhite : crossBlack)}') no-repeat center center / contain;
    transition: 0.2s;
    min-width: auto;

    &:hover {
        transform: rotate(90deg);
    }
`;

export default PopUpWrapper;
